import React, { useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { CallbackModal } from '../components/CallbackModal';
import { CheckCircle2, Calendar, MessageCircle, ArrowLeft, PoundSterling, Play, Sparkles, ArrowRight, Clock, Heart, ShieldCheck, Languages, MapPin, Phone, Plus, Minus } from 'lucide-react';
import { useTranslation } from '../i18n';
import { CallbackForm } from '../components/CallbackForm';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const serviceToPriceAnchor: Record<string, string> = {
  fillings: 'fillings-restorations',
  'root-canal': 'root-canal',
  crowns: 'crowns-veneers',
  hygiene: 'dental-hygiene',
  whitening: 'whitening',
  extraction: 'extractions',
  'smile-makeover': 'crowns-veneers',
  cosmetic: 'injectables'
};
type ServiceDetail = {
  intro: {
    en: string;
    ua: string;
    ru: string;
    ro: string;
  };
  included: {
    en: string[];
    ua: string[];
    ru: string[];
    ro: string[];
  };
  time: {
    en: string;
    ua: string;
    ru: string;
    ro: string;
  };
  comfort: {
    en: string;
    ua: string;
    ru: string;
    ro: string;
  };
  aftercare: {
    en: string;
    ua: string;
    ru: string;
    ro: string;
  };
};
const serviceDetails: Record<string, ServiceDetail> = {
  fillings: {
    intro: {
      en: 'Tooth-coloured fillings restore teeth affected by decay or small fractures, keeping a natural look.',
      ua: 'Фотополімерні пломби відновлюють зуби, уражені карієсом або невеликими тріщинами, зберігаючи природний вигляд.',
      ru: 'Фотополимерные пломбы восстанавливают зубы, пораженные кариесом или небольшими трещинами, сохраняя естественный вид.',
      ro: 'Plombele în culoarea dintelui restaurează dinții afectați de carii sau fracturi mici, păstrând un aspect natural.'
    },
    included: {
      en: ['Clinical assessment (+ X-ray if needed)', 'Gentle decay removal and isolation', 'Layered composite restoration', 'Bite adjustment and polishing'],
      ua: ['Клінічна оцінка (+ рентген за потреби)', 'Делікатне видалення карієсу та ізоляція', 'Пошарова композитна реставрація', 'Корекція прикусу та полірування'],
      ru: ['Клиническая оценка (+ рентген при необходимости)', 'Деликатное удаление кариеса и изоляция', 'Послойная композитная реставрация', 'Коррекция прикуса и полировка'],
      ro: ['Evaluare clinică (+ radiografie dacă este necesar)', 'Îndepărtarea delicată a cariei și izolare', 'Restaurare compozită stratificată', 'Ajustarea ocluziei și lustruire']
    },
    time: {
      en: '30–60 minutes',
      ua: '30–60 хвилин',
      ru: '30–60 минут',
      ro: '30–60 minute'
    },
    comfort: {
      en: 'Local anaesthetic available; minimally invasive approach.',
      ua: 'Місцева анестезія; мінімально інвазивний підхід.',
      ru: 'Местная анестезия; минимально инвазивный подход.',
      ro: 'Anestezie locală disponibilă; abordare minim invazivă.'
    },
    aftercare: {
      en: 'Avoid hard biting until numbness wears off; contact us if the bite feels "high".',
      ua: 'Уникайте твердої їжі до зникнення оніміння; зверніться до нас, якщо прикус здається "високим".',
      ru: 'Избегайте твёрдой пищи до исчезновения онемения; свяжитесь с нами, если прикус кажется "высоким".',
      ro: 'Evitați mestecarea dură până dispare amorțeala; contactați-ne dacă ocluzia pare „înaltă".'
    }
  },
  'root-canal': {
    intro: {
      en: 'Aesthetic restorations rebuild tooth shape, edges and colour for a harmonious smile.',
      ua: 'Естетичні реставрації відновлюють форму, краї та колір зуба для гармонійної посмішки.',
      ru: 'Эстетические реставрации восстанавливают форму, края и цвет зуба для гармоничной улыбки.',
      ro: 'Restaurările estetice reconstruiesc forma, marginile și culoarea dintelui pentru un zâmbet armonios.'
    },
    included: {
      en: ['Smile assessment and shade selection', 'Layered composite sculpting', 'High-gloss polishing for natural enamel shine'],
      ua: ['Оцінка посмішки та підбір відтінку', 'Пошарове моделювання композитом', 'Полірування до блиску натуральної емалі'],
      ru: ['Оценка улыбки и подбор оттенка', 'Послойное моделирование композитом', 'Полировка до блеска натуральной эмали'],
      ro: ['Evaluarea zâmbetului și selectarea nuanței', 'Sculptare compozită stratificată', 'Lustruire de înaltă strălucire pentru un aspect natural al smalțului']
    },
    time: {
      en: '45–90 minutes per tooth',
      ua: '45–90 хвилин на зуб',
      ru: '45–90 минут на зуб',
      ro: '45–90 minute per dinte'
    },
    comfort: {
      en: 'Usually comfortable; anaesthetic if required.',
      ua: 'Зазвичай комфортно; анестезія за потреби.',
      ru: 'Обычно комфортно; анестезия при необходимости.',
      ro: 'De obicei confortabil; anestezie dacă este necesar.'
    },
    aftercare: {
      en: 'Reduce staining foods/drinks for 24–48h; keep regular hygiene visits.',
      ua: 'Обмежте фарбуючі продукти/напої на 24–48 год; відвідуйте гігієніста регулярно.',
      ru: 'Ограничьте красящие продукты/напитки на 24–48 ч; посещайте гигиениста регулярно.',
      ro: 'Reduceți alimentele/băuturile colorante timp de 24–48h; mențineți vizitele regulate de igienă.'
    }
  },
  crowns: {
    intro: {
      en: 'Crowns protect weakened teeth; veneers enhance colour and form with a refined, natural result.',
      ua: 'Коронки захищають ослаблені зуби; вініри покращують колір та форму з витонченим, природним результатом.',
      ru: 'Коронки защищают ослабленные зубы; виниры улучшают цвет и форму с утончённым, естественным результатом.',
      ro: 'Coroanele protejează dinții slăbiți; fațetele îmbunătățesc culoarea și forma cu un rezultat rafinat și natural.'
    },
    included: {
      en: ['Consultation + digital planning', 'Scan/impressions and lab fabrication', 'Try-in, bite check, cementation', 'Aftercare guidance'],
      ua: ['Консультація + цифрове планування', 'Сканування/зліпки та виготовлення в лабораторії', 'Примірка, перевірка прикусу, цементування', 'Рекомендації по догляду'],
      ru: ['Консультация + цифровое планирование', 'Сканирование/слепки и изготовление в лаборатории', 'Примерка, проверка прикуса, цементирование', 'Рекомендации по уходу'],
      ro: ['Consultație + planificare digitală', 'Scanare/amprente și fabricare în laborator', 'Probă, verificarea ocluziei, cimentare', 'Îndrumare post-tratament']
    },
    time: {
      en: 'Typically 2 visits',
      ua: 'Зазвичай 2 візити',
      ru: 'Обычно 2 визита',
      ro: 'De obicei 2 vizite'
    },
    comfort: {
      en: 'Local anaesthetic; sensitivity managed carefully.',
      ua: 'Місцева анестезія; чутливість контролюється ретельно.',
      ru: 'Местная анестезия; чувствительность контролируется тщательно.',
      ro: 'Anestezie locală; sensibilitatea gestionată cu atenție.'
    },
    aftercare: {
      en: 'Avoid very hard foods initially; night guard recommended if clenching.',
      ua: 'Спочатку уникайте дуже твердої їжі; нічна капа рекомендована при бруксизмі.',
      ru: 'Сначала избегайте очень твёрдой пищи; ночная капа рекомендована при бруксизме.',
      ro: 'Evitați alimentele foarte dure inițial; gutieră de noapte recomandată în caz de bruxism.'
    }
  },
  hygiene: {
    intro: {
      en: 'Deep cleaning removes plaque and tartar to protect gums and improve breath freshness.',
      ua: 'Глибока чистка видаляє наліт та зубний камінь для захисту ясен та свіжості дихання.',
      ru: 'Глубокая чистка удаляет налёт и зубной камень для защиты дёсен и свежести дыхания.',
      ro: 'Curățarea profundă îndepărtează placa și tartrul pentru a proteja gingiile și a îmbunătăți prospețimea respirației.'
    },
    included: {
      en: ['Gum screening', 'Scaling and polishing (AirFlow if offered)', 'Personalised home-care plan'],
      ua: ['Скринінг ясен', 'Скейлінг та полірування (AirFlow за наявності)', 'Індивідуальний план домашнього догляду'],
      ru: ['Скрининг дёсен', 'Скейлинг и полировка (AirFlow при наличии)', 'Индивидуальный план домашнего ухода'],
      ro: ['Screening gingival', 'Detartraj și lustruire (AirFlow dacă este disponibil)', 'Plan personalizat de îngrijire la domiciliu']
    },
    time: {
      en: '30–60 minutes',
      ua: '30–60 хвилин',
      ru: '30–60 минут',
      ro: '30–60 minute'
    },
    comfort: {
      en: 'Gentle technique; numbing gel if sensitive.',
      ua: 'Делікатна техніка; знеболюючий гель при чутливості.',
      ru: 'Деликатная техника; обезболивающий гель при чувствительности.',
      ro: 'Tehnică delicată; gel anestezic dacă există sensibilitate.'
    },
    aftercare: {
      en: 'Mild gum tenderness possible; follow interdental cleaning routine.',
      ua: 'Можлива легка чутливість ясен; дотримуйтесь міжзубної чистки.',
      ru: 'Возможна лёгкая чувствительность дёсен; соблюдайте межзубную чистку.',
      ro: 'Posibilă sensibilitate ușoară a gingiilor; urmați rutina de curățare interdentară.'
    }
  },
  whitening: {
    intro: {
      en: 'Professional whitening brightens the smile safely with controlled protocols.',
      ua: 'Професійне відбілювання безпечно освітлює посмішку за контрольованими протоколами.',
      ru: 'Профессиональное отбеливание безопасно осветляет улыбку по контролируемым протоколам.',
      ro: 'Albirea profesională luminează zâmbetul în siguranță cu protocoale controlate.'
    },
    included: {
      en: ['Suitability check and shade assessment', 'In-clinic whitening and/or home plan', 'Sensitivity management advice'],
      ua: ['Перевірка придатності та оцінка відтінку', 'Клінічне відбілювання та/або домашній план', 'Поради щодо управління чутливістю'],
      ru: ['Проверка пригодности и оценка оттенка', 'Клиническое отбеливание и/или домашний план', 'Советы по управлению чувствительностью'],
      ro: ['Verificarea eligibilității și evaluarea nuanței', 'Albire în clinică și/sau plan la domiciliu', 'Sfaturi pentru gestionarea sensibilității']
    },
    time: {
      en: '60–90 minutes in clinic or 1–2 weeks at home',
      ua: '60–90 хвилин у клініці або 1–2 тижні вдома',
      ru: '60–90 минут в клинике или 1–2 недели дома',
      ro: '60–90 minute în clinică sau 1–2 săptămâni acasă'
    },
    comfort: {
      en: 'Controlled protocols; sensitivity tips provided.',
      ua: 'Контрольовані протоколи; надаються поради щодо чутливості.',
      ru: 'Контролируемые протоколы; предоставляются советы по чувствительности.',
      ro: 'Protocoale controlate; sfaturi pentru sensibilitate oferite.'
    },
    aftercare: {
      en: '"White diet" 24–48h; limit coffee/tea/red wine; follow sensitivity tips.',
      ua: '"Біла дієта" 24–48 год; обмежте каву/чай/червоне вино; дотримуйтесь порад.',
      ru: '"Белая диета" 24–48 ч; ограничьте кофе/чай/красное вино; следуйте советам.',
      ro: '„Dietă albă" 24–48h; limitați cafeaua/ceaiul/vinul roșu; urmați sfaturile pentru sensibilitate.'
    }
  },
  extraction: {
    intro: {
      en: 'Gentle tooth removal when a tooth cannot be saved, with a focus on safe healing and comfort.',
      ua: 'Делікатне видалення зуба, коли його неможливо зберегти, з акцентом на безпечне загоєння та комфорт.',
      ru: 'Деликатное удаление зуба, когда его невозможно сохранить, с акцентом на безопасное заживление и комфорт.',
      ro: 'Extracție delicată a dintelui când acesta nu poate fi salvat, cu accent pe vindecare sigură și confort.'
    },
    included: {
      en: ['Examination + X-ray', 'Local anaesthetic', 'Extraction + haemostasis', 'Clear post-op instructions'],
      ua: ['Огляд + рентген', 'Місцева анестезія', 'Видалення + гемостаз', 'Чіткі післяопераційні інструкції'],
      ru: ['Осмотр + рентген', 'Местная анестезия', 'Удаление + гемостаз', 'Чёткие послеоперационные инструкции'],
      ro: ['Examinare + radiografie', 'Anestezie locală', 'Extracție + hemostază', 'Instrucțiuni clare post-operatorii']
    },
    time: {
      en: '20–60 minutes',
      ua: '20–60 хвилин',
      ru: '20–60 минут',
      ro: '20–60 minute'
    },
    comfort: {
      en: 'Strong local anaesthetic; supportive approach for anxious patients.',
      ua: 'Сильна місцева анестезія; підтримуючий підхід для тривожних пацієнтів.',
      ru: 'Сильная местная анестезия; поддерживающий подход для тревожных пациентов.',
      ro: 'Anestezie locală puternică; abordare de sprijin pentru pacienții anxioși.'
    },
    aftercare: {
      en: 'Soft food, no smoking 48–72h, cold compress day 1; contact us if severe pain/swelling.',
      ua: "М'яка їжа, не палити 48–72 год, холодний компрес 1-й день; зверніться при сильному болю/набряку.",
      ru: 'Мягкая пища, не курить 48–72 ч, холодный компресс 1-й день; обратитесь при сильной боли/отёке.',
      ro: 'Alimente moi, fără fumat 48–72h, compresă rece ziua 1; contactați-ne în caz de durere/umflătură severă.'
    }
  },
  cosmetic: {
    intro: {
      en: 'Non-surgical aesthetic treatments focused on natural, refreshed results.',
      ua: 'Нехірургічні естетичні процедури, спрямовані на природний, свіжий результат.',
      ru: 'Нехирургические эстетические процедуры, направленные на естественный, свежий результат.',
      ro: 'Tratamente estetice non-chirurgicale axate pe rezultate naturale și proaspete.'
    },
    included: {
      en: ['Medical assessment + contraindications', 'Treatment plan and consent', 'Aftercare instructions'],
      ua: ['Медична оцінка + протипоказання', 'План лікування та згода', 'Інструкції по догляду'],
      ru: ['Медицинская оценка + противопоказания', 'План лечения и согласие', 'Инструкции по уходу'],
      ro: ['Evaluare medicală + contraindicații', 'Plan de tratament și consimțământ', 'Instrucțiuni post-tratament']
    },
    time: {
      en: '20–45 minutes',
      ua: '20–45 хвилин',
      ru: '20–45 минут',
      ro: '20–45 minute'
    },
    comfort: {
      en: 'Only by qualified medical professional; safety-first approach.',
      ua: 'Тільки кваліфікованим медичним спеціалістом; підхід "безпека перш за все".',
      ru: 'Только квалифицированным медицинским специалистом; подход "безопасность прежде всего".',
      ro: 'Doar de către un profesionist medical calificat; abordare cu siguranța pe primul loc.'
    },
    aftercare: {
      en: 'Avoid intense exercise/sauna for 24h; follow provided guidance.',
      ua: 'Уникайте інтенсивних вправ/сауни протягом 24 год; дотримуйтесь наданих рекомендацій.',
      ru: 'Избегайте интенсивных упражнений/сауны в течение 24 ч; следуйте предоставленным рекомендациям.',
      ro: 'Evitați exercițiile intense/sauna timp de 24h; urmați îndrumările oferite.'
    }
  }
};
// UA-optimised H1 titles with location keywords
const uaServiceTitles: Record<string, string> = {
  fillings: 'Пломбування зубів у Лондоні (Angel)',
  'root-canal': 'Лікування каналів у Лондоні (Angel)',
  crowns: 'Коронки та протезування в Лондоні (Angel)',
  hygiene: 'Професійна гігієна зубів у Лондоні (Angel)',
  whitening: 'Відбілювання зубів у Лондоні (Angel)',
  extraction: 'Видалення зубів у Лондоні (Angel)',
  'smile-makeover': 'Smile Makeover у Лондоні — Dentico Clinic (Angel)',
  cosmetic: "Ін'єкційна косметологія в Лондоні (Angel)"
};
// Related services mapping
const relatedServices: Record<string, string[]> = {
  fillings: ['root-canal', 'crowns', 'hygiene'],
  'root-canal': ['fillings', 'crowns', 'extraction'],
  crowns: ['fillings', 'smile-makeover', 'whitening'],
  hygiene: ['whitening', 'fillings', 'smile-makeover'],
  whitening: ['hygiene', 'smile-makeover', 'crowns'],
  extraction: ['root-canal', 'crowns', 'fillings'],
  'smile-makeover': ['whitening', 'crowns', 'cosmetic'],
  cosmetic: ['smile-makeover', 'whitening', 'hygiene']
};
// Per-service SEO metadata (title + description) and a location-aware H1.
type Lang = 'en' | 'ua' | 'ru' | 'ro';
const serviceSeo: Record<string, {
  title: Record<Lang, string>;
  description: Record<Lang, string>;
  h1: Record<Lang, string>;
}> = {
  fillings: {
    title: {
      en: 'Dental Fillings in Angel, London | Dentico Clinic',
      ua: 'Пломбування зубів у Лондоні (Angel) | Dentico Clinic',
      ru: 'Пломбирование зубов в Лондоне (Angel) | Dentico Clinic',
      ro: 'Plombe Dentare în Angel, Londra | Dentico Clinic'
    },
    description: {
      en: 'Tooth-coloured fillings and dental restorations in Angel, London. Restore damaged teeth with natural-looking results at Dentico Clinic.',
      ua: 'Пломби в колір зуба та реставрації в Angel, Лондон. Відновіть пошкоджені зуби з природним результатом у Dentico Clinic.',
      ru: 'Пломбы в цвет зуба и реставрации в Angel, Лондон. Восстановите повреждённые зубы с естественным результатом в Dentico Clinic.',
      ro: 'Plombe în culoarea dintelui și restaurări în Angel, Londra. Restaurați dinții deteriorați cu rezultate naturale la Dentico Clinic.'
    },
    h1: {
      en: 'Dental Fillings in Angel, London',
      ua: 'Пломбування зубів у Лондоні (Angel)',
      ru: 'Пломбирование зубов в Лондоне (Angel)',
      ro: 'Plombe Dentare în Angel, Londra'
    }
  },
  'root-canal': {
    title: {
      en: 'Root Canal Treatment in Angel, London | Dentico Clinic',
      ua: 'Лікування каналів у Лондоні (Angel) | Dentico Clinic',
      ru: 'Лечение каналов в Лондоне (Angel) | Dentico Clinic',
      ro: 'Tratament de Canal în Angel, Londra | Dentico Clinic'
    },
    description: {
      en: 'Root canal treatment in Angel, London for infected or painful teeth. Dentico Clinic provides careful, modern endodontic care.',
      ua: 'Лікування каналів у Лондоні (Angel) при інфікованих або болючих зубах. Dentico Clinic надає дбайливу, сучасну ендодонтичну допомогу.',
      ru: 'Лечение каналов в Лондоне (Angel) при инфицированных или болезненных зубах. Dentico Clinic предоставляет бережную эндодонтическую помощь.',
      ro: 'Tratament de canal în Angel, Londra pentru dinți infectați sau dureroși. Dentico Clinic oferă îngrijire endodontică modernă și atentă.'
    },
    h1: {
      en: 'Root Canal Treatment in Angel, London',
      ua: 'Лікування каналів у Лондоні (Angel)',
      ru: 'Лечение каналов в Лондоне (Angel)',
      ro: 'Tratament de Canal în Angel, Londra'
    }
  },
  crowns: {
    title: {
      en: 'Dental Crowns in Angel, London | Dentico Clinic',
      ua: 'Коронки у Лондоні (Angel) | Dentico Clinic',
      ru: 'Коронки в Лондоне (Angel) | Dentico Clinic',
      ro: 'Coroane Dentare în Angel, Londra | Dentico Clinic'
    },
    description: {
      en: 'Dental crowns in Angel, London to protect weakened teeth and restore function, comfort and appearance at Dentico Clinic.',
      ua: 'Коронки у Лондоні (Angel) для захисту ослаблених зубів та відновлення функції, комфорту й естетики у Dentico Clinic.',
      ru: 'Коронки в Лондоне (Angel) для защиты ослабленных зубов и восстановления функции, комфорта и эстетики в Dentico Clinic.',
      ro: 'Coroane dentare în Angel, Londra pentru a proteja dinții slăbiți și a reda funcția, confortul și aspectul la Dentico Clinic.'
    },
    h1: {
      en: 'Dental Crowns in Angel, London',
      ua: 'Коронки у Лондоні (Angel)',
      ru: 'Коронки в Лондоне (Angel)',
      ro: 'Coroane Dentare în Angel, Londra'
    }
  },
  hygiene: {
    title: {
      en: 'Dental Hygienist in Angel, London | Dentico Clinic',
      ua: 'Гігієніст у Лондоні (Angel) | Dentico Clinic',
      ru: 'Гигиенист в Лондоне (Angel) | Dentico Clinic',
      ro: 'Igienist Dentar în Angel, Londra | Dentico Clinic'
    },
    description: {
      en: 'Book a dental hygiene appointment in Angel, London. Gentle professional cleaning, gum care and airflow-style polishing at Dentico Clinic.',
      ua: 'Запишіться на гігієну в Angel, Лондон. Делікатна професійна чистка, догляд за яснами та полірування Airflow у Dentico Clinic.',
      ru: 'Запишитесь на гигиену в Angel, Лондон. Деликатная профессиональная чистка, уход за дёснами и полировка Airflow в Dentico Clinic.',
      ro: 'Programați o ședință de igienă dentară în Angel, Londra. Curățare profesională delicată, îngrijirea gingiilor și lustruire Airflow la Dentico Clinic.'
    },
    h1: {
      en: 'Dental Hygiene in Angel, London',
      ua: 'Професійна гігієна зубів у Лондоні (Angel)',
      ru: 'Профессиональная гигиена зубов в Лондоне (Angel)',
      ro: 'Igienă Dentară în Angel, Londra'
    }
  },
  whitening: {
    title: {
      en: 'Teeth Whitening in Angel, London | Dentico Clinic',
      ua: 'Відбілювання зубів у Лондоні (Angel) | Dentico Clinic',
      ru: 'Отбеливание зубов в Лондоне (Angel) | Dentico Clinic',
      ro: 'Albirea Dinților în Angel, Londra | Dentico Clinic'
    },
    description: {
      en: 'Professional teeth whitening in Angel, London. Brighten your smile safely with personalised whitening advice from Dentico Clinic.',
      ua: 'Професійне відбілювання зубів у Лондоні (Angel). Безпечно освітліть усмішку з індивідуальними рекомендаціями Dentico Clinic.',
      ru: 'Профессиональное отбеливание зубов в Лондоне (Angel). Безопасно осветлите улыбку с индивидуальными рекомендациями Dentico Clinic.',
      ro: 'Albire dentară profesională în Angel, Londra. Luminați-vă zâmbetul în siguranță cu sfaturi personalizate de la Dentico Clinic.'
    },
    h1: {
      en: 'Teeth Whitening in Angel, London',
      ua: 'Відбілювання зубів у Лондоні (Angel)',
      ru: 'Отбеливание зубов в Лондоне (Angel)',
      ro: 'Albirea Dinților în Angel, Londra'
    }
  },
  extraction: {
    title: {
      en: 'Tooth Extraction in Angel, London | Dentico Clinic',
      ua: 'Видалення зубів у Лондоні (Angel) | Dentico Clinic',
      ru: 'Удаление зубов в Лондоне (Angel) | Dentico Clinic',
      ro: 'Extracție Dentară în Angel, Londra | Dentico Clinic'
    },
    description: {
      en: 'Gentle tooth extraction in Angel, London, including urgent extractions and oral surgery assessment at Dentico Clinic.',
      ua: 'Делікатне видалення зубів у Лондоні (Angel), включаючи термінові видалення та оцінку для хірургії у Dentico Clinic.',
      ru: 'Деликатное удаление зубов в Лондоне (Angel), включая срочные удаления и оценку для хирургии в Dentico Clinic.',
      ro: 'Extracție dentară delicată în Angel, Londra, inclusiv extracții urgente și evaluare pentru chirurgie orală la Dentico Clinic.'
    },
    h1: {
      en: 'Tooth Extraction in Angel, London',
      ua: 'Видалення зубів у Лондоні (Angel)',
      ru: 'Удаление зубов в Лондоне (Angel)',
      ro: 'Extracție Dentară în Angel, Londra'
    }
  },
  'smile-makeover': {
    title: {
      en: 'Smile Makeover in Angel, London | Dentico Clinic',
      ua: 'Smile Makeover у Лондоні (Angel) | Dentico Clinic',
      ru: 'Smile Makeover в Лондоне (Angel) | Dentico Clinic',
      ro: 'Transformarea Zâmbetului în Angel, Londra | Dentico Clinic'
    },
    description: {
      en: 'Smile makeover in Angel, London combining veneers, whitening and bonding for natural-looking results at Dentico Clinic.',
      ua: 'Smile makeover у Лондоні (Angel): вініри, відбілювання та бондинг для природного результату у Dentico Clinic.',
      ru: 'Smile makeover в Лондоне (Angel): виниры, отбеливание и бондинг для естественного результата в Dentico Clinic.',
      ro: 'Transformarea zâmbetului în Angel, Londra combinând fațete, albire și bonding pentru rezultate naturale la Dentico Clinic.'
    },
    h1: {
      en: 'Smile Makeover in Angel, London',
      ua: 'Smile Makeover у Лондоні (Angel)',
      ru: 'Smile Makeover в Лондоне (Angel)',
      ro: 'Transformarea Zâmbetului în Angel, Londra'
    }
  },
  cosmetic: {
    title: {
      en: 'Composite Bonding in Angel, London | Dentico Clinic',
      ua: 'Композитний бондинг у Лондоні (Angel) | Dentico Clinic',
      ru: 'Композитный бондинг в Лондоне (Angel) | Dentico Clinic',
      ro: 'Bonding Compozit în Angel, Londra | Dentico Clinic'
    },
    description: {
      en: 'Composite bonding in Angel, London to improve tooth shape, edges and smile confidence with natural-looking results.',
      ua: 'Композитний бондинг у Лондоні (Angel) для покращення форми, країв зубів та впевненості в усмішці з природним результатом.',
      ru: 'Композитный бондинг в Лондоне (Angel) для улучшения формы, краёв зубов и уверенности в улыбке с естественным результатом.',
      ro: 'Bonding compozit în Angel, Londra pentru a îmbunătăți forma, marginile dinților și încrederea în zâmbet cu rezultate naturale.'
    },
    h1: {
      en: 'Composite Bonding in Angel, London',
      ua: 'Композитний бондинг у Лондоні (Angel)',
      ru: 'Композитный бондинг в Лондоні (Angel)',
      ro: 'Bonding Compozit în Angel, Londra'
    }
  }
};
// Multilingual FAQ per service
const serviceFaqsData: Record<string, {
  q: Record<'en' | 'ua' | 'ru' | 'ro', string>;
  a: Record<'en' | 'ua' | 'ru' | 'ro', string>;
}[]> = {
  fillings: [{
    q: {
      en: 'How much does a filling cost in London?',
      ua: 'Скільки коштує пломба в Лондоні?',
      ru: 'Сколько стоит пломба в Лондоне?',
      ro: 'Cât costă o plombă în Londra?'
    },
    a: {
      en: 'The price depends on the size and depth of the decay and the material used. After an examination, we will provide the cost and treatment options.',
      ua: 'Ціна залежить від розміру та глибини карієсу і матеріалу. Після огляду ми озвучимо вартість і варіанти лікування.',
      ru: 'Цена зависит от размера и глубины кариеса и материала. После осмотра мы озвучим стоимость и варианты лечения.',
      ro: 'Prețul depinde de dimensiunea și adâncimea cariei și de materialul folosit. După o examinare, vom oferi costul și opțiunile de tratament.'
    }
  }, {
    q: {
      en: 'Does it hurt to get a filling?',
      ua: 'Чи боляче лікувати карієс?',
      ru: 'Больно ли лечить кариес?',
      ro: 'Doare să pui o plombă?'
    },
    a: {
      en: 'In most cases, treatment is performed under anesthesia and is comfortable. We explain every step and work as gently as possible.',
      ua: 'У більшості випадків лікування проводиться під анестезією і є комфортним. Ми пояснюємо кожен етап і працюємо максимально делікатно.',
      ru: 'В большинстве случаев лечение проводится под анестезией и является комфортным. Мы объясняем каждый этап и работаем максимально деликатно.',
      ro: 'În majoritatea cazurilor, tratamentul se efectuează sub anestezie și este confortabil. Explicăm fiecare pas și lucrăm cât mai delicat posibil.'
    }
  }, {
    q: {
      en: 'How do I know if I need a filling?',
      ua: 'Як зрозуміти, що карієс потрібно лікувати?',
      ru: 'Как понять, что кариес нужно лечить?',
      ro: 'Cum știu dacă am nevoie de o plombă?'
    },
    a: {
      en: 'Signs include sensitivity, pain with sweet/cold foods, darkening, or food getting stuck. An accurate diagnosis is made during an examination.',
      ua: 'Ознаки: чутливість, біль при солодкому/холодному, потемніння, застрягання їжі. Точний діагноз визначається на огляді.',
      ru: 'Признаки: чувствительность, боль при сладком/холодном, потемнение, застревание пищи. Точный диагноз определяется на осмотре.',
      ro: 'Semnele includ sensibilitate, durere la alimente dulci/reci, întunecare sau alimente blocate. Un diagnostic precis se face în timpul unei examinări.'
    }
  }, {
    q: {
      en: 'Can I get a tooth-coloured filling?',
      ua: 'Чи можна зробити пломбу в колір зуба?',
      ru: 'Можно ли сделать пломбу в цвет зуба?',
      ro: 'Pot obține o plombă de culoarea dintelui?'
    },
    a: {
      en: 'Yes. We use modern composite materials that are matched to the shade of your tooth for a natural look.',
      ua: 'Так. Ми використовуємо сучасні композитні матеріали, які підбираються під відтінок зуба для природного вигляду.',
      ru: 'Да. Мы используем современные композитные материалы, которые подбираются под оттенок зуба для естественного вида.',
      ro: 'Da. Folosim materiale compozite moderne care sunt potrivite cu nuanța dintelui dumneavoastră pentru un aspect natural.'
    }
  }, {
    q: {
      en: 'Where are you located?',
      ua: 'Де ви знаходиться?',
      ru: 'Где вы находитесь?',
      ro: 'Unde vă aflați?'
    },
    a: {
      en: 'Dentico Clinic: 360–364 City Road, London EC1V 2PY (Angel / Islington).',
      ua: 'Dentico Clinic: 360–364 City Road, London EC1V 2PY (Angel / Islington).',
      ru: 'Dentico Clinic: 360–364 City Road, London EC1V 2PY (Angel / Islington).',
      ro: 'Dentico Clinic: 360–364 City Road, Londra EC1V 2PY (Angel / Islington).'
    }
  }],
  'root-canal': [{
    q: {
      en: 'When is a root canal needed?',
      ua: 'Коли потрібно лікувати канали?',
      ru: 'Когда нужно лечить каналы?',
      ro: 'Când este necesar un tratament de canal?'
    },
    a: {
      en: 'When there is nerve inflammation or infection in the root canals. Common symptoms: severe pain, sensitivity, swelling, pain when biting.',
      ua: 'Коли є запалення нерва або інфекція в кореневих каналах. Часті симптоми: сильний біль, чутливість, набряк, біль при накусуванні.',
      ru: 'Когда есть воспаление нерва или инфекция в корневых каналах. Частые симптомы: сильная боль, чувствительность, отек, боль при накусывании.',
      ro: 'Când există inflamație a nervului sau infecție în canalele radiculare. Simptome comune: durere severă, sensibilitate, umflare, durere la mușcătură.'
    }
  }, {
    q: {
      en: 'Does a root canal hurt?',
      ua: 'Чи боляче лікувати канали?',
      ru: 'Больно ли лечить каналы?',
      ro: 'Doare un tratament de canal?'
    },
    a: {
      en: 'Treatment is performed under anesthesia. In most cases, the procedure is painless, and there may be temporary discomfort afterwards.',
      ua: 'Лікування проводиться під анестезією. У більшості випадків процедура безболісна, а після можливий тимчасовий дискомфорт.',
      ru: 'Лечение проводится под анестезией. В большинстве случаев процедура безболезненна, а после возможен временный дискомфорт.',
      ro: 'Tratamentul se efectuează sub anestezie. În majoritatea cazurilor, procedura este nedureroasă, iar ulterior poate exista un disconfort temporar.'
    }
  }, {
    q: {
      en: 'How much does a root canal cost in London?',
      ua: 'Скільки коштує лікування каналів у Лондоні?',
      ru: 'Сколько стоит лечение каналов в Лондоне?',
      ro: 'Cât costă un tratament de canal în Londra?'
    },
    a: {
      en: 'The cost depends on the number of canals and the complexity of the case. After an examination, we provide a treatment plan and estimate.',
      ua: 'Вартість залежить від кількості каналів та складності випадку. Після огляду ми надаємо план лікування і кошторис.',
      ru: 'Стоимость зависит от количества каналов и сложности случая. После осмотра мы предоставляем план лечения и смету.',
      ro: 'Costul depinde de numărul de canale și de complexitatea cazului. După o examinare, oferim un plan de tratament și o estimare.'
    }
  }, {
    q: {
      en: 'How many visits are needed?',
      ua: 'Скільки візитів потрібно?',
      ru: 'Сколько визитов нужно?',
      ro: 'Câte vizite sunt necesare?'
    },
    a: {
      en: 'Sometimes treatment can be completed in 1 visit, but complex cases may require 2 visits. This is determined after diagnosis.',
      ua: 'Іноді лікування можна завершити за 1 візит, але складні випадки можуть потребувати 2 візитів. Це визначається після діагностики.',
      ru: 'Иногда лечение можно завершить за 1 визит, но сложные случаи могут потребовать 2 визитов. Это определяется после диагностики.',
      ro: 'Uneori tratamentul poate fi finalizat într-o singură vizită, dar cazurile complexe pot necesita 2 vizite. Acest lucru este determinat după diagnosticare.'
    }
  }, {
    q: {
      en: 'Do you offer emergency appointments for acute pain?',
      ua: 'Чи робите термінові візити при гострому болю?',
      ru: 'Делаете ли срочные визиты при острой боли?',
      ro: 'Oferiți programări de urgență pentru durere acută?'
    },
    a: {
      en: 'Yes. If you have severe pain, contact us as soon as possible — we will try to find the nearest available time.',
      ua: "Так. Якщо у вас сильний біль, зв'яжіться з нами якнайшвидше — ми постараємося знайти найближчий час.",
      ru: 'Да. Если у вас сильная боль, свяжитесь с нами как можно скорее — мы постараемся найти ближайшее время.',
      ro: 'Da. Dacă aveți dureri severe, contactați-ne cât mai curând posibil — vom încerca să găsim cel mai apropiat timp disponibil.'
    }
  }],
  crowns: [{
    q: {
      en: 'When is a dental crown needed?',
      ua: 'Коли потрібна коронка на зуб?',
      ru: 'Когда нужна коронка на зуб?',
      ro: 'Când este necesară o coroană dentară?'
    },
    a: {
      en: 'A crown is needed when a tooth is severely damaged, after a root canal, or when long-term restoration of shape and function is required.',
      ua: 'Коронка потрібна, коли зуб сильно зруйнований, після лікування каналів або коли потрібне довготривале відновлення форми та функції.',
      ru: 'Коронка нужна, когда зуб сильно разрушен, после лечения каналов или когда требуется долгосрочное восстановление формы и функции.',
      ro: 'O coroană este necesară atunci când un dinte este grav deteriorat, după un tratament de canal sau când este necesară restaurarea pe termen lung a formei și funcției.'
    }
  }, {
    q: {
      en: 'How much does a crown cost in London?',
      ua: 'Скільки коштує коронка в Лондоні?',
      ru: 'Сколько стоит коронка в Лондоне?',
      ro: 'Cât costă o coroană în Londra?'
    },
    a: {
      en: 'The cost depends on the material and the complexity of the case. After an examination, we create a treatment plan and estimate.',
      ua: 'Вартість залежить від матеріалу та складності випадку. Після огляду ми складаємо план лікування і кошторис.',
      ru: 'Стоимость зависит от материала и сложности случая. После осмотра мы составляем план лечения и смету.',
      ro: 'Costul depinde de material și de complexitatea cazului. După o examinare, creăm un plan de tratament și o estimare.'
    }
  }, {
    q: {
      en: 'How long does it take to get a crown?',
      ua: 'Скільки часу займає встановлення коронки?',
      ru: 'Сколько времени занимает установка коронки?',
      ro: 'Cât durează să obțineți o coroană?'
    },
    a: {
      en: 'Usually 2 visits are required: tooth preparation and impressions/scan, then crown cementation. Timelines depend on the clinical situation.',
      ua: 'Зазвичай потрібно 2 візити: підготовка зуба та зняття відбитків/скан, потім фіксація коронки. Терміни залежать від клінічної ситуації.',
      ru: 'Обычно требуется 2 визита: подготовка зуба и снятие слепков/скан, затем фиксация коронки. Сроки зависят от клинической ситуации.',
      ro: 'De obicei sunt necesare 2 vizite: pregătirea dintelui și amprente/scanare, apoi cimentarea coroanei. Termenele depind de situația clinică.'
    }
  }, {
    q: {
      en: 'Does it hurt to get a crown?',
      ua: 'Чи боляче ставити коронку?',
      ru: 'Больно ли ставить коронку?',
      ro: 'Doare să pui o coroană?'
    },
    a: {
      en: 'The procedure is performed under anesthesia. Discomfort is minimal, and afterwards, there may be short-term sensations of adaptation.',
      ua: 'Процедура проводиться під анестезією. Дискомфорт мінімальний, а після — можливі короткочасні відчуття адаптації.',
      ru: 'Процедура проводится под анестезией. Дискомфорт минимальный, а после — возможны кратковременные ощущения адаптации.',
      ro: 'Procedura se efectuează sub anestezie. Disconfortul este minim, iar ulterior pot exista senzații pe termen scurt de adaptare.'
    }
  }, {
    q: {
      en: 'Where is Dentico Clinic located?',
      ua: 'Де знаходиться Dentico Clinic?',
      ru: 'Где находится Dentico Clinic?',
      ro: 'Unde se află Dentico Clinic?'
    },
    a: {
      en: 'Angel / Islington, Central London (EC1V). 360–364 City Road, London EC1V 2PY.',
      ua: 'Angel / Islington, Central London (EC1V). 360–364 City Road, London EC1V 2PY.',
      ru: 'Angel / Islington, Central London (EC1V). 360–364 City Road, London EC1V 2PY.',
      ro: 'Angel / Islington, Central London (EC1V). 360–364 City Road, Londra EC1V 2PY.'
    }
  }],
  hygiene: [{
    q: {
      en: 'How often should I see a dental hygienist?',
      ua: 'Як часто потрібно робити професійну чистку зубів?',
      ru: 'Как часто нужно делать профессиональную чистку зубов?',
      ro: 'Cât de des ar trebui să văd un igienist dentar?'
    },
    a: {
      en: 'We usually recommend 1-2 times a year. The frequency may vary depending on gum health and plaque buildup rate.',
      ua: 'Зазвичай рекомендуємо 1–2 рази на рік. Частота може змінюватися залежно від стану ясен та швидкості утворення нальоту.',
      ru: 'Обычно рекомендуем 1–2 раза в год. Частота может меняться в зависимости от состояния десен и скорости образования налета.',
      ro: 'De obicei recomandăm de 1-2 ori pe an. Frecvența poate varia în funcție de sănătatea gingiilor și de rata de acumulare a plăcii.'
    }
  }, {
    q: {
      en: 'Is dental hygiene treatment painful?',
      ua: 'Чи боляче робити Airflow або ультразвукову чистку?',
      ru: 'Больно ли делать Airflow или ультразвуковую чистку?',
      ro: 'Este dureros tratamentul de igienă dentară?'
    },
    a: {
      en: 'Most patients tolerate the procedure comfortably. For increased sensitivity, we select a gentler mode.',
      ua: 'Більшість пацієнтів переносить процедуру комфортно. За підвищеної чутливості ми підбираємо більш делікатний режим.',
      ru: 'Большинство пациентов переносит процедуру комфортно. При повышенной чувствительности мы подбираем более деликатный режим.',
      ro: 'Majoritatea pacienților tolerează procedura confortabil. Pentru sensibilitate crescută, selectăm un mod mai blând.'
    }
  }, {
    q: {
      en: 'What is the difference between Airflow and regular cleaning?',
      ua: 'Чим відрізняється Airflow від звичайної чистки?',
      ru: 'Чем отличается Airflow от обычной чистки?',
      ro: 'Care este diferența dintre Airflow și curățarea obișnuită?'
    },
    a: {
      en: 'Airflow helps remove soft plaque and pigmentation (coffee/tea), while ultrasound removes tartar. Often, the methods are combined.',
      ua: "Airflow допомагає видалити м'який наліт і пігментацію (кава/чай), а ультразвук — зубний камінь. Часто методики комбінують.",
      ru: 'Airflow помогает удалить мягкий налет и пигментацию (кофе/чай), а ультразвук — зубной камень. Часто методики комбинируют.',
      ro: 'Airflow ajută la îndepărtarea plăcii moi și a pigmentării (cafea/ceai), în timp ce ultrasunetele îndepărtează tartrul. Adesea, metodele sunt combinate.'
    }
  }, {
    q: {
      en: 'Do teeth get lighter after cleaning?',
      ua: 'Чи освітлюються зуби після чистки?',
      ru: 'Осветляются ли зубы после чистки?',
      ro: 'Dinții devin mai deschiși la culoare după curățare?'
    },
    a: {
      en: 'After plaque removal, teeth may look lighter because stains are removed. But this does not replace professional whitening.',
      ua: 'Після зняття нальоту зуби можуть виглядати світлішими, бо забруднення видалені. Але це не замінює професійне відбілювання.',
      ru: 'После снятия налета зубы могут выглядеть светлее, так как загрязнения удалены. Но это не заменяет профессиональное отбеливание.',
      ro: 'După îndepărtarea plăcii, dinții pot părea mai deschiși la culoare deoarece petele sunt îndepărtate. Dar acest lucru nu înlocuiește albirea profesională.'
    }
  }, {
    q: {
      en: 'How to book a hygiene appointment in Angel, London?',
      ua: 'Як записатися на гігієну в Angel, London?',
      ru: 'Как записаться на гигиену в Angel, London?',
      ro: 'Cum să rezervați o programare de igienă în Angel, Londra?'
    },
    a: {
      en: 'Book online or message us on WhatsApp/Telegram. Dentico Clinic is next to Angel Station (EC1V).',
      ua: 'Запишіться онлайн або напишіть нам у WhatsApp/Telegram. Dentico Clinic — поруч з Angel Station (EC1V).',
      ru: 'Запишитесь онлайн или напишите нам в WhatsApp/Telegram. Dentico Clinic — рядом с Angel Station (EC1V).',
      ro: 'Rezervați online sau scrieți-ne pe WhatsApp/Telegram. Dentico Clinic este lângă stația Angel (EC1V).'
    }
  }],
  whitening: [{
    q: {
      en: 'Is professional teeth whitening safe?',
      ua: 'Чи безпечне професійне відбілювання зубів?',
      ru: 'Безопасно ли профессиональное отбеливание зубов?',
      ro: 'Este sigură albirea profesională a dinților?'
    },
    a: {
      en: 'Yes, provided there are correct indications and dentist supervision. Before the procedure, we assess the condition of the enamel and gums.',
      ua: 'Так, за умови правильних показань та контролю стоматолога. Перед процедурою ми оцінюємо стан емалі та ясен.',
      ru: 'Да, при условии правильных показаний и контроля стоматолога. Перед процедурой мы оцениваем состояние эмали и десен.',
      ro: 'Da, cu condiția să existe indicații corecte și supravegherea medicului dentist. Înainte de procedură, evaluăm starea smalțului și a gingiilor.'
    }
  }, {
    q: {
      en: 'How long does the whitening effect last?',
      ua: 'Скільки тримається ефект відбілювання?',
      ru: 'Сколько держится эффект отбеливания?',
      ro: 'Cât durează efectul de albire?'
    },
    a: {
      en: 'Duration depends on dietary habits and care. We provide recommendations on how to maintain the result longer.',
      ua: 'Тривалість залежить від харчових звичок та догляду. Ми даємо рекомендації, як зберегти результат надовше.',
      ru: 'Длительность зависит от пищевых привычек и ухода. Мы даем рекомендации, как сохранить результат дольше.',
      ro: 'Durata depinde de obiceiurile alimentare și de îngrijire. Oferim recomandări despre cum să mențineți rezultatul mai mult timp.'
    }
  }, {
    q: {
      en: 'Will there be sensitivity after whitening?',
      ua: 'Чи буде чутливість після відбілювання?',
      ru: 'Будет ли чувствительность после отбеливания?',
      ro: 'Va exista sensibilitate după albire?'
    },
    a: {
      en: 'There may be temporary sensitivity. It usually passes quickly, and we select care and recommendations for your comfort.',
      ua: 'Може бути тимчасова чутливість. Зазвичай вона проходить швидко, а ми підбираємо догляд та рекомендації для комфорту.',
      ru: 'Может быть временная чувствительность. Обычно она проходит быстро, а мы подбираем уход и рекомендации для комфорта.',
      ro: 'Poate exista sensibilitate temporară. De obicei trece repede, iar noi selectăm îngrijirea și recomandările pentru confortul dumneavoastră.'
    }
  }, {
    q: {
      en: 'Can I whiten teeth with fillings/crowns?',
      ua: 'Чи можна відбілювати зуби з пломбами/коронками?',
      ru: 'Можно ли отбеливать зубы с пломбами/коронками?',
      ro: 'Pot albi dinții cu plombe/coroane?'
    },
    a: {
      en: 'Whitening does not change the color of fillings and crowns. During the consultation, we explain the options so the color looks harmonious.',
      ua: 'Відбілювання не змінює колір пломб і коронок. На консультації ми пояснюємо варіанти, щоб колір виглядав гармонійно.',
      ru: 'Отбеливание не меняет цвет пломб и коронок. На консультации мы объясняем варианты, чтобы цвет выглядел гармонично.',
      ro: 'Albirea nu schimbă culoarea plombelor și coroanelor. În timpul consultației, explicăm opțiunile astfel încât culoarea să arate armonios.'
    }
  }, {
    q: {
      en: 'Where to get teeth whitening in London (Angel)?',
      ua: 'Де зробити відбілювання зубів у Лондоні (Angel)?',
      ru: 'Где сделать отбеливание зубов в Лондоне (Angel)?',
      ro: 'Unde să faceți albirea dinților în Londra (Angel)?'
    },
    a: {
      en: 'Dentico Clinic is located in Angel / Islington, Central London (EC1V). Booking is available online or via WhatsApp/Telegram.',
      ua: 'Dentico Clinic знаходиться в Angel / Islington, Central London (EC1V). Запис доступний онлайн або через WhatsApp/Telegram.',
      ru: 'Dentico Clinic находится в Angel / Islington, Central London (EC1V). Запись доступна онлайн или через WhatsApp/Telegram.',
      ro: 'Dentico Clinic este situată în Angel / Islington, Central London (EC1V). Rezervarea este disponibilă online sau prin WhatsApp/Telegram.'
    }
  }],
  extraction: [{
    q: {
      en: 'What should I do if I have severe toothache?',
      ua: 'Що робити, якщо сильно болить зуб?',
      ru: 'Что делать, если сильно болит зуб?',
      ro: 'Ce ar trebui să fac dacă am o durere de dinți severă?'
    },
    a: {
      en: 'Contact us as soon as possible for an urgent examination. We will determine the cause of the pain and offer treatment.',
      ua: "Якнайшвидше зв'яжіться з нами для невідкладного огляду. Ми визначимо причину болю та запропонуємо лікування.",
      ru: 'Как можно скорее свяжитесь с нами для неотложного осмотра. Мы определим причину боли и предложим лечение.',
      ro: 'Contactați-ne cât mai curând posibil pentru o examinare urgentă. Vom determina cauza durerii și vom oferi tratament.'
    }
  }, {
    q: {
      en: 'Do you accept urgent same-day appointments?',
      ua: 'Чи приймаєте ви терміново в той же день?',
      ru: 'Принимаете ли вы срочно в тот же день?',
      ro: 'Acceptați programări urgente în aceeași zi?'
    },
    a: {
      en: 'Yes, whenever possible we offer the nearest available time. Message us on WhatsApp/Telegram or call.',
      ua: 'Так, за можливості ми пропонуємо найближчий доступний час. Напишіть у WhatsApp/Telegram або зателефонуйте.',
      ru: 'Да, по возможности мы предлагаем ближайшее доступное время. Напишите в WhatsApp/Telegram или позвоните.',
      ro: 'Da, ori de câte ori este posibil oferim cel mai apropiat timp disponibil. Scrieți-ne pe WhatsApp/Telegram sau sunați.'
    }
  }, {
    q: {
      en: 'How much is an emergency appointment in London?',
      ua: 'Скільки коштує екстрений прийом у Лондоні?',
      ru: 'Сколько стоит экстренный прием в Лондоне?',
      ro: 'Cât costă o programare de urgență în Londra?'
    },
    a: {
      en: 'The cost depends on the necessary procedures and diagnostics. After the examination, we will explain the treatment options and prices.',
      ua: 'Вартість залежить від необхідних процедур та діагностики. Після огляду ми пояснимо варіанти лікування та ціни.',
      ru: 'Стоимость зависит от необходимых процедур и диагностики. После осмотра мы объясним варианты лечения и цены.',
      ro: 'Costul depinde de procedurile și diagnosticele necesare. După examinare, vom explica opțiunile de tratament și prețurile.'
    }
  }, {
    q: {
      en: 'What symptoms are considered an emergency?',
      ua: 'Які симптоми вважаються невідкладними?',
      ru: 'Какие симптомы считаются неотложными?',
      ro: 'Ce simptome sunt considerate o urgență?'
    },
    a: {
      en: 'Severe pain, swelling, pus, fever, pain when swallowing, tooth trauma/chip — these are reasons to seek urgent care.',
      ua: 'Сильний біль, набряк, гній, підвищена температура, біль при ковтанні, травма/скол зуба — це привід звернутися терміново.',
      ru: 'Сильная боль, отек, гной, повышенная температура, боль при глотании, травма/скол зуба — это повод обратиться срочно.',
      ro: 'Durere severă, umflare, puroi, febră, durere la înghițire, traumă/ciobire a dintelui — acestea sunt motive pentru a solicita îngrijire urgentă.'
    }
  }, {
    q: {
      en: 'Where are you located?',
      ua: 'Де ви знаходиться?',
      ru: 'Где вы находитесь?',
      ro: 'Unde vă aflați?'
    },
    a: {
      en: 'Dentico Clinic: 360–364 City Road, London EC1V 2PY (Angel / Islington), next to Angel Station.',
      ua: 'Dentico Clinic: 360–364 City Road, London EC1V 2PY (Angel / Islington), поруч з Angel Station.',
      ru: 'Dentico Clinic: 360–364 City Road, London EC1V 2PY (Angel / Islington), рядом с Angel Station.',
      ro: 'Dentico Clinic: 360–364 City Road, Londra EC1V 2PY (Angel / Islington), lângă stația Angel.'
    }
  }],
  'smile-makeover': [{
    q: {
      en: 'How much do veneers cost in London?',
      ua: 'Скільки коштують вініри в Лондоні?',
      ru: 'Сколько стоят виниры в Лондоне?',
      ro: 'Cât costă fațetele în Londra?'
    },
    a: {
      en: 'The price depends on the material, the number of teeth, and the complexity of the aesthetic plan. After a consultation, we offer a treatment plan and estimate.',
      ua: 'Ціна залежить від матеріалу, кількості зубів та складності естетичного плану. Після консультації ми пропонуємо план лікування та кошторис.',
      ru: 'Цена зависит от материала, количества зубов и сложности эстетического плана. После консультации мы предлагаем план лечения и смету.',
      ro: 'Prețul depinde de material, numărul de dinți și complexitatea planului estetic. După o consultație, oferim un plan de tratament și o estimare.'
    }
  }, {
    q: {
      en: 'Do teeth need to be filed down for veneers?',
      ua: 'Чи потрібно обточувати зуби під вініри?',
      ru: 'Нужно ли обтачивать зубы под виниры?',
      ro: 'Trebuie șlefuiți dinții pentru fațete?'
    },
    a: {
      en: 'In most cases, minimal enamel preparation is required, but the extent depends on the shape, bite, and desired result. We always choose the most conservative approach.',
      ua: 'У більшості випадків потрібна мінімальна підготовка емалі, але обсяг залежить від форми, прикусу та бажаного результату. Ми завжди обираємо максимально щадний підхід.',
      ru: 'В большинстве случаев требуется минимальная подготовка эмали, но объем зависит от формы, прикуса и желаемого результата. Мы всегда выбираем максимально щадящий подход.',
      ro: 'În majoritatea cazurilor, este necesară o pregătire minimă a smalțului, dar amploarea depinde de formă, mușcătură și rezultatul dorit. Alegem întotdeauna cea mai conservatoare abordare.'
    }
  }, {
    q: {
      en: 'How long do veneers last?',
      ua: 'Скільки служать вініри?',
      ru: 'Сколько служат виниры?',
      ro: 'Cât durează fațetele?'
    },
    a: {
      en: 'Lifespan depends on the material, bite, and care. During the consultation, we explain how to extend the longevity of the work.',
      ua: 'Термін служби залежить від матеріалу, прикусу та догляду. Під час консультації ми пояснюємо, як продовжити довговічність роботи.',
      ru: 'Срок службы зависит от материала, прикуса и ухода. Во время консультации мы объясняем, как продлить долговечность работы.',
      ro: 'Durata de viață depinde de material, mușcătură și îngrijire. În timpul consultației, explicăm cum să prelungiți longevitatea lucrării.'
    }
  }, {
    q: {
      en: 'Can I get a natural result, without "too white" teeth?',
      ua: 'Чи можна зробити природний результат, без "надто білих" зубів?',
      ru: 'Можно ли сделать естественный результат, без "слишком белых" зубов?',
      ro: 'Pot obține un rezultat natural, fără dinți "prea albi"?'
    },
    a: {
      en: 'Yes. We select the shape and color individually so the smile looks natural.',
      ua: 'Так. Ми підбираємо форму та колір індивідуально, щоб усмішка виглядала натурально.',
      ru: 'Да. Мы подбираем форму и цвет индивидуально, чтобы улыбка выглядела натурально.',
      ro: 'Da. Selectăm forma și culoarea individual, astfel încât zâmbetul să arate natural.'
    }
  }, {
    q: {
      en: 'Can I get a consultation in my language?',
      ua: 'Чи можна отримати консультацію українською мовою?',
      ru: 'Можно ли получить консультацию на русском языке?',
      ro: 'Pot primi o consultație în limba mea?'
    },
    a: {
      en: 'Yes, at Dentico Clinic, communication in English, Ukrainian, Russian, or Romanian is possible during consultation and treatment.',
      ua: 'Так, у Dentico Clinic можливе спілкування українською мовою під час консультації та лікування.',
      ru: 'Да, в Dentico Clinic возможно общение на русском языке во время консультации и лечения.',
      ro: 'Da, la Dentico Clinic, comunicarea în engleză, ucraineană, rusă sau română este posibilă în timpul consultației și tratamentului.'
    }
  }],
  cosmetic: [{
    q: {
      en: 'How much does a dental implant cost in London?',
      ua: 'Скільки коштує імплантація зуба в Лондоні?',
      ru: 'Сколько стоит имплантация зуба в Лондоне?',
      ro: 'Cât costă un implant dentar în Londra?'
    },
    a: {
      en: 'The cost of implantation depends on the clinical situation, implant type, and the need for additional procedures (e.g., bone grafting). After a consultation, we provide a transparent treatment plan and estimate.',
      ua: 'Вартість імплантації залежить від клінічної ситуації, типу імпланта та необхідності додаткових процедур (наприклад, кісткова пластика). Після консультації ми складаємо прозорий план лікування та кошторис.',
      ru: 'Стоимость имплантации зависит от клинической ситуации, типа импланта и необходимости дополнительных процедур (например, костная пластика). После консультации мы составляем прозрачный план лечения и смету.',
      ro: 'Costul implantării depinde de situația clinică, tipul de implant și necesitatea unor proceduri suplimentare (de exemplu, adiție de os). După o consultație, oferim un plan de tratament transparent și o estimare.'
    }
  }, {
    q: {
      en: 'Does it hurt to get an implant?',
      ua: 'Чи боляче ставити імплант?',
      ru: 'Больно ли ставить имплант?',
      ro: 'Doare să pui un implant?'
    },
    a: {
      en: 'The procedure is performed under local anesthesia. During implant placement, most patients feel no pain, and afterwards, there may be discomfort, which is controlled by recommendations and painkillers.',
      ua: 'Процедура проводиться під місцевою анестезією. Під час встановлення імпланта більшість пацієнтів не відчувають болю, а після — можливий дискомфорт, який контролюється рекомендаціями та знеболенням.',
      ru: 'Процедура проводится под местной анестезией. Во время установки импланта большинство пациентов не чувствуют боли, а после — возможен дискомфорт, который контролируется рекомендациями и обезболиванием.',
      ro: 'Procedura se efectuează sub anestezie locală. În timpul plasării implantului, majoritatea pacienților nu simt durere, iar ulterior poate exista disconfort, care este controlat prin recomandări și analgezice.'
    }
  }, {
    q: {
      en: 'How long does implantation take?',
      ua: 'Скільки часу займає імплантація?',
      ru: 'Сколько времени занимает имплантация?',
      ro: 'Cât durează implantarea?'
    },
    a: {
      en: 'Timelines depend on the case. Usually, a consultation and diagnostics are performed first, then implant placement, and after a healing period, crown placement. The exact schedule is determined individually.',
      ua: 'Терміни залежать від випадку. Зазвичай спочатку проводиться консультація та діагностика, потім встановлення імпланта, а після періоду загоєння — встановлення коронки. Точний графік визначається індивідуально.',
      ru: 'Сроки зависят от случая. Обычно сначала проводится консультация и диагностика, затем установка импланта, а после периода заживления — установка коронки. Точный график определяется индивидуально.',
      ro: 'Termenele depind de caz. De obicei, se efectuează mai întâi o consultație și diagnosticare, apoi plasarea implantului, iar după o perioadă de vindecare, plasarea coroanei. Programul exact este determinat individual.'
    }
  }, {
    q: {
      en: 'Is implantation suitable if the tooth has been missing for a long time?',
      ua: 'Чи підходить імплантація, якщо давно немає зуба?',
      ru: 'Подходит ли имплантация, если давно нет зуба?',
      ro: 'Este potrivită implantarea dacă dintele lipsește de mult timp?'
    },
    a: {
      en: 'Yes, but an assessment of bone condition may be needed. During the consultation, we check bone volume and discuss restoration options if necessary.',
      ua: "Так, але може знадобитися оцінка стану кістки. Під час консультації ми перевіряємо об'єм кістки та за потреби обговорюємо варіанти відновлення.",
      ru: 'Да, но может потребоваться оценка состояния кости. Во время консультации мы проверяем объем кости и при необходимости обсуждаем варианты восстановления.',
      ro: 'Da, dar poate fi necesară o evaluare a stării osului. În timpul consultației, verificăm volumul osos și discutăm opțiunile de restaurare dacă este necesar.'
    }
  }, {
    q: {
      en: 'Where is the clinic for implantation located?',
      ua: 'Де знаходиться клініка для імплантації?',
      ru: 'Где находится клиника для имплантации?',
      ro: 'Unde se află clinica pentru implantare?'
    },
    a: {
      en: 'Dentico Clinic is located in Angel / Islington, Central London (EC1V). Address: 360–364 City Road, London EC1V 2PY, next to Angel Station.',
      ua: 'Dentico Clinic розташована в Angel / Islington, Central London (EC1V). Адреса: 360–364 City Road, London EC1V 2PY, поруч з Angel Station.',
      ru: 'Dentico Clinic расположена в Angel / Islington, Central London (EC1V). Адрес: 360–364 City Road, London EC1V 2PY, рядом с Angel Station.',
      ro: 'Dentico Clinic este situată în Angel / Islington, Central London (EC1V). Adresa: 360–364 City Road, Londra EC1V 2PY, lângă stația Angel.'
    }
  }]
};
function ServiceFaqItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-beige/60 last:border-b-0" data-id="element-3727">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-3.5 text-left focus:outline-none group" data-id="element-3728">
        <span className="text-base font-medium text-charcoal group-hover:text-forest-600 transition-colors" data-id="element-3729">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-5 h-5 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-forest-600 text-white' : 'bg-stone-200 text-stone-500'}`} data-id="element-3730">
          {isOpen ? <Minus className="w-3 h-3" data-id="element-3731" /> : <Plus className="w-3 h-3" data-id="element-3732" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-3.5' : 'max-h-0 opacity-0'}`} data-id="element-3733">
        <p className="text-charcoal-light text-sm leading-relaxed" data-id="element-3734">{answer}</p>
      </div>
    </div>;
}
export function ServiceDetailPage() {
  const {
    serviceId
  } = useParams();
  const {
    t,
    lang,
    getArray
  } = useTranslation();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const servicesData: Record<string, {
    image: string;
  }> = {
    fillings: {
      image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=1200&q=80'
    },
    'root-canal': {
      image: 'https://images.unsplash.com/photo-1598256989800-fe5f95da9787?auto=format&fit=crop&w=1200&q=80'
    },
    crowns: {
      image: 'https://images.unsplash.com/photo-1606265752439-1f18756aa5fc?auto=format&fit=crop&w=1200&q=80'
    },
    hygiene: {
      image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80'
    },
    whitening: {
      image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=1200&q=80'
    },
    extraction: {
      image: 'https://images.unsplash.com/photo-1598256989800-fe5f95da9787?auto=format&fit=crop&w=1200&q=80'
    },
    'smile-makeover': {
      image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=1200&q=80'
    },
    cosmetic: {
      image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=1200&q=80'
    }
  };
  const translationKey = serviceId?.replace(/-([a-z])/g, g => g[1].toUpperCase());
  if (!serviceId || !servicesData[serviceId]) {
    return <Navigate to={`/${lang}/services`} replace data-id="element-3735" />;
  }
  const serviceImage = servicesData[serviceId].image;
  const seo = serviceId ? serviceSeo[serviceId] : undefined;
  const seoLang = lang as Lang;
  const title = seo ? seo.h1[seoLang] : lang === 'ua' && serviceId && uaServiceTitles[serviceId] ? uaServiceTitles[serviceId] : t(`services.${translationKey}.title`);
  const seoTitle = seo ? seo.title[seoLang] : t(`services.${translationKey}.title`);
  const seoDescription = seo ? seo.description[seoLang] : t(`services.${translationKey}.fullDesc`);
  const description = t(`services.${translationKey}.fullDesc`);
  const benefits = getArray(`services.${translationKey}.benefits`);
  const detail = serviceDetails[serviceId];
  const isSmileMakeover = serviceId === 'smile-makeover';
  const labels = {
    included: {
      en: "What's included",
      ua: 'Що входить',
      ru: 'Что включено',
      ro: 'Ce este inclus'
    },
    time: {
      en: 'Duration',
      ua: 'Тривалість',
      ru: 'Длительность',
      ro: 'Durată'
    },
    comfort: {
      en: 'Comfort & anaesthesia',
      ua: 'Комфорт та анестезія',
      ru: 'Комфорт и анестезия',
      ro: 'Confort și anestezie'
    },
    aftercare: {
      en: 'Aftercare',
      ua: 'Догляд після процедури',
      ru: 'Уход после процедуры',
      ro: 'Îngrijire post-tratament'
    }
  };
  const beforeAfterCases = [{
    before: 'https://cdn.magicpatterns.com/uploads/shQ5ZRHdqwygEh9jr68EUz/IMG_8186.jpg',
    after: 'https://cdn.magicpatterns.com/uploads/pnzB7AsEyjzSLFCXJDe779/IMG_9004.jpg',
    description: lang === 'en' ? 'Smile transformation with veneers' : lang === 'ua' ? 'Трансформація посмішки вінірами' : lang === 'ro' ? 'Transformarea zâmbetului cu fațete' : 'Трансформация улыбки винирами'
  }, {
    before: 'https://cdn.magicpatterns.com/uploads/9Sh8YYorQi4Z3aQpsAEiEW/IMG_8183.jpg',
    after: 'https://cdn.magicpatterns.com/uploads/6FZYJz1gkBZaYnPMEjSFwB/IMG_9008.jpg',
    description: lang === 'en' ? 'Complete smile makeover' : lang === 'ua' ? 'Повна трансформація посмішки' : lang === 'ro' ? 'Transformare completă a zâmbetului' : 'Полная трансформация улыбки'
  }];
  const related = serviceId && relatedServices[serviceId] ? relatedServices[serviceId] : [];
  const serviceFaqs = serviceId && serviceFaqsData[serviceId] ? serviceFaqsData[serviceId] : [];
  const serviceSchema = serviceId ? {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: title,
    description: detail ? detail.intro[lang] : description,
    procedureType: 'http://schema.org/NoninvasiveProcedure',
    howPerformed: detail ? detail.comfort[lang] : undefined,
    preparation: detail ? detail.aftercare[lang] : undefined,
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  } : null;
  const faqSchema = serviceFaqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: serviceFaqs.map(faq => ({
      '@type': 'Question',
      name: faq.q[lang as 'en' | 'ua' | 'ru' | 'ro'] || faq.q.en,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a[lang as 'en' | 'ua' | 'ru' | 'ro'] || faq.a.en
      }
    }))
  } : null;
  return <Layout title={seoTitle} description={seoDescription} data-id="element-3736">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-3737" />

      {serviceSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(serviceSchema)
    }} data-id="element-3738" />}
      {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-3739" />}

      {/* Full-width hero image */}
      <div className="relative" data-id="element-3740">
        <div className="h-[300px] sm:h-[400px] lg:h-[480px] w-full overflow-hidden" data-id="element-3741">
          <img src={serviceImage} alt={title} className="w-full h-full object-cover" data-id="element-3742" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" data-id="element-3743" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 lg:p-12" data-id="element-3744">
          <div className="max-w-7xl mx-auto" data-id="element-3745">
            <Link to={`/${lang}/services`} className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-4 text-sm" data-id="element-3746">
              <ArrowLeft className="w-4 h-4" data-id="element-3747" />
              {t('services.viewAll')}
            </Link>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white" data-id="element-3748">
              {title}
            </h1>
            {lang === 'ua' && <div className="mt-3 inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-sm" data-id="element-3749">
                <Languages className="w-4 h-4" data-id="element-3750" />
                Консультація українською · Angel, Central London
              </div>}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-3751">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16" data-id="element-3752">
          {/* Main content */}
          <div className="lg:col-span-3 space-y-8" data-id="element-3753">
            {detail ? <p className="text-lg text-charcoal-light leading-relaxed" data-id="element-3754">
                {detail.intro[lang]}
              </p> : <p className="text-lg text-charcoal-light leading-relaxed" data-id="element-3755">
                {description}
              </p>}

            {detail && <div data-id="element-3756">
                <h2 className="text-lg font-bold text-charcoal mb-4" data-id="element-3757">
                  {labels.included[lang]}
                </h2>
                <div className="space-y-2.5" data-id="element-3758">
                  {detail.included[lang].map((item, idx) => <div key={idx} className="flex items-start gap-3 p-3 bg-ivory-200 rounded-xl" data-id="element-3759">
                      <CheckCircle2 className="w-5 h-5 text-forest-600 flex-shrink-0 mt-0.5" data-id="element-3760" />
                      <span className="text-sm text-charcoal" data-id="element-3761">{item}</span>
                    </div>)}
                </div>
              </div>}

            {detail && <div className="grid sm:grid-cols-3 gap-4" data-id="element-3762">
                <div className="bg-ivory-200 rounded-xl p-4" data-id="element-3763">
                  <div className="flex items-center gap-2 mb-2" data-id="element-3764">
                    <Clock className="w-4 h-4 text-forest-600" data-id="element-3765" />
                    <span className="text-xs font-semibold text-forest-700 uppercase tracking-wide" data-id="element-3766">
                      {labels.time[lang]}
                    </span>
                  </div>
                  <p className="text-sm text-charcoal" data-id="element-3767">{detail.time[lang]}</p>
                </div>
                <div className="bg-ivory-200 rounded-xl p-4" data-id="element-3768">
                  <div className="flex items-center gap-2 mb-2" data-id="element-3769">
                    <Heart className="w-4 h-4 text-forest-600" data-id="element-3770" />
                    <span className="text-xs font-semibold text-forest-700 uppercase tracking-wide" data-id="element-3771">
                      {labels.comfort[lang]}
                    </span>
                  </div>
                  <p className="text-sm text-charcoal" data-id="element-3772">
                    {detail.comfort[lang]}
                  </p>
                </div>
                <div className="bg-ivory-200 rounded-xl p-4" data-id="element-3773">
                  <div className="flex items-center gap-2 mb-2" data-id="element-3774">
                    <ShieldCheck className="w-4 h-4 text-forest-600" data-id="element-3775" />
                    <span className="text-xs font-semibold text-forest-700 uppercase tracking-wide" data-id="element-3776">
                      {labels.aftercare[lang]}
                    </span>
                  </div>
                  <p className="text-sm text-charcoal" data-id="element-3777">
                    {detail.aftercare[lang]}
                  </p>
                </div>
              </div>}

            {!detail && benefits.length > 0 && <div className="grid sm:grid-cols-2 gap-3" data-id="element-3778">
                {benefits.map((benefit, idx) => <div key={idx} className="flex items-center gap-3 p-3 bg-ivory-200 rounded-xl" data-id="element-3779">
                    <CheckCircle2 className="w-5 h-5 text-forest-600 flex-shrink-0" data-id="element-3780" />
                    <span className="text-sm text-charcoal" data-id="element-3781">{benefit}</span>
                  </div>)}
              </div>}
          </div>

          {/* Sidebar - sticky */}
          <div className="lg:col-span-2" data-id="element-3782">
            <div className="lg:sticky lg:top-24 space-y-4" data-id="element-3783">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="block" data-id="element-3784">
                <Button size="lg" fullWidth className="gap-2" data-id="element-3785">
                  <Calendar className="w-5 h-5" data-id="element-3786" />
                  {t('servicesPage.bookOnlineNow')}
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="block" data-id="element-3787">
                <Button variant="whatsapp" size="lg" fullWidth className="gap-2" data-id="element-3788">
                  <MessageCircle className="w-5 h-5" data-id="element-3789" />
                  WhatsApp
                </Button>
              </a>
              <div className="block" data-id="element-3790">
                <Button variant="outline" size="lg" fullWidth className="gap-2" onClick={() => setIsCallbackOpen(true)} data-id="element-3791">
                  <Phone className="w-5 h-5" data-id="element-3792" />
                  {lang === 'en' ? 'Request a Call' : lang === 'ua' ? 'Замовити дзвінок' : lang === 'ro' ? 'Solicitați un Apel' : 'Заказать звонок'}
                </Button>
              </div>
              <a href="tel:+447345197820" className="block" data-id="element-3793">
                <Button variant="outline" size="lg" fullWidth className="gap-2" data-id="element-3794">
                  <Phone className="w-5 h-5" data-id="element-3795" />
                  {lang === 'en' ? 'Call Clinic' : lang === 'ua' ? 'Зателефонувати' : lang === 'ro' ? 'Sunați la Clinică' : 'Позвонить в клинику'}
                </Button>
              </a>

              <Link to={`/${lang}/prices#${serviceToPriceAnchor[serviceId!] || ''}`} className="flex items-center gap-3 p-4 bg-ivory-200 rounded-xl hover:bg-stone-200 transition-colors group" data-id="element-3796">
                <div className="p-2 bg-forest-600 rounded-lg text-white" data-id="element-3797">
                  <PoundSterling className="w-5 h-5" data-id="element-3798" />
                </div>
                <div className="flex-1" data-id="element-3799">
                  <p className="font-semibold text-charcoal text-sm" data-id="element-3800">
                    {t('nav.prices')}
                  </p>
                  <p className="text-xs text-stone-500" data-id="element-3801">
                    {lang === 'en' ? 'View transparent pricing' : lang === 'ua' ? 'Переглянути ціни' : lang === 'ro' ? 'Vedeți prețurile transparente' : 'Посмотреть цены'}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-stone-500 group-hover:text-forest-600 transition-colors" data-id="element-3802" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Smile Makeover Special Sections */}
      {isSmileMakeover && <>
          {/* Treatments Grid */}
          <div className="bg-ivory-200 py-16" data-id="element-3803">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3804">
              <h2 className="text-2xl lg:text-3xl font-bold text-charcoal mb-8 text-center" data-id="element-3805">
                {lang === 'en' ? 'Our Smile Makeover Treatments' : lang === 'ua' ? 'Наші процедури Smile Makeover' : lang === 'ro' ? 'Tratamentele Noastre de Transformare a Zâmbetului' : 'Наши процедуры Smile Makeover'}
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-3806">
                {[{
              name: lang === 'en' ? 'Porcelain Veneers' : lang === 'ua' ? 'Керамічні вініри' : lang === 'ro' ? 'Fațete din Porțelan' : 'Керамические виниры',
              desc: lang === 'en' ? 'Ultra-thin shells for a perfect smile' : lang === 'ua' ? 'Ультратонкі накладки для ідеальної посмішки' : lang === 'ro' ? 'Învelișuri ultra-subțiri pentru un zâmbet perfect' : 'Ультратонкие накладки для идеальной улыбки'
            }, {
              name: lang === 'en' ? 'Composite Bonding' : lang === 'ua' ? 'Композитні накладки' : lang === 'ro' ? 'Bonding Compozit' : 'Композитные накладки',
              desc: lang === 'en' ? 'Reshape and restore naturally' : lang === 'ua' ? 'Природна реставрація зубів' : lang === 'ro' ? 'Remodelare și restaurare naturală' : 'Естественная реставрация зубов'
            }, {
              name: lang === 'en' ? 'Teeth Whitening' : lang === 'ua' ? 'Відбілювання зубів' : lang === 'ro' ? 'Albirea Dinților' : 'Отбеливание зубов',
              desc: lang === 'en' ? 'Professional brighter smile' : lang === 'ua' ? 'Професійне відбілювання' : lang === 'ro' ? 'Zâmbet profesional mai strălucitor' : 'Профессиональное отбеливание'
            }, {
              name: lang === 'en' ? 'Inlays & Onlays' : lang === 'ua' ? 'Вкладки та накладки' : lang === 'ro' ? 'Inlay-uri și Onlay-uri' : 'Вкладки и накладки',
              desc: lang === 'en' ? 'Custom ceramic restorations' : lang === 'ua' ? 'Індивідуальні керамічні реставрації' : lang === 'ro' ? 'Restaurări ceramice personalizate' : 'Индивидуальные керамические реставрации'
            }, {
              name: lang === 'en' ? 'Muscle Relaxation' : lang === 'ua' ? "Розслаблення м'язів" : lang === 'ro' ? 'Relaxare Musculară' : 'Расслабление мышц',
              desc: lang === 'en' ? 'Facial aesthetics complement' : lang === 'ua' ? 'Естетика обличчя' : lang === 'ro' ? 'Complement de estetică facială' : 'Эстетика лица'
            }, {
              name: lang === 'en' ? '3D Scanning' : lang === 'ua' ? '3D-сканування' : lang === 'ro' ? 'Scanare 3D' : '3D-сканирование',
              desc: lang === 'en' ? 'Digital planning for predictable results' : lang === 'ua' ? 'Цифрове планування результату' : lang === 'ro' ? 'Planificare digitală pentru rezultate previzibile' : 'Цифровое планирование результата'
            }].map((treatment, idx) => <div key={idx} className="bg-ivory p-5 rounded-xl border border-beige/60" data-id="element-3807">
                    <Sparkles className="w-6 h-6 text-forest-600 mb-3" data-id="element-3808" />
                    <h3 className="font-semibold text-charcoal text-sm mb-1" data-id="element-3809">
                      {treatment.name}
                    </h3>
                    <p className="text-xs text-stone-500" data-id="element-3810">{treatment.desc}</p>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Before & After Gallery */}
          <div className="bg-ivory py-16" data-id="element-3811">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3812">
              <h2 className="text-2xl lg:text-3xl font-bold text-charcoal mb-8 text-center" data-id="element-3813">
                {t('services.smileMakeover.gallery.title')}
              </h2>
              <div className="grid md:grid-cols-2 gap-6" data-id="element-3814">
                {beforeAfterCases.map((caseItem, idx) => <div key={idx} className="bg-ivory rounded-2xl overflow-hidden shadow-lg border border-beige/60" data-id="element-3815">
                    <div className="grid grid-cols-2" data-id="element-3816">
                      <div className="relative aspect-square" data-id="element-3817">
                        <img src={caseItem.before} alt={`Before - Case ${idx + 1}`} className="absolute inset-0 w-full h-full object-cover" data-id="element-3818" />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3" data-id="element-3819">
                          <span className="text-white text-xs font-medium uppercase tracking-wide" data-id="element-3820">
                            {lang === 'en' ? 'Before' : lang === 'ua' ? 'До' : lang === 'ro' ? 'Înainte' : 'До'}
                          </span>
                        </div>
                      </div>
                      <div className="relative aspect-square" data-id="element-3821">
                        <img src={caseItem.after} alt={`After - Case ${idx + 1}`} className="absolute inset-0 w-full h-full object-cover" data-id="element-3822" />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3" data-id="element-3823">
                          <span className="text-white text-xs font-medium uppercase tracking-wide" data-id="element-3824">
                            {lang === 'en' ? 'After' : lang === 'ua' ? 'Після' : lang === 'ro' ? 'După' : 'После'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 text-center bg-[#EFE8DE]" data-id="element-3825">
                      <p className="text-sm font-medium text-[#2F4A3F]" data-id="element-3826">
                        {caseItem.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Video Section */}
          <div className="bg-ivory-200 py-16" data-id="element-3827">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3828">
              <h2 className="text-2xl lg:text-3xl font-bold text-charcoal mb-8 text-center" data-id="element-3829">
                {t('services.smileMakeover.video.title')}
              </h2>
              <div className="grid md:grid-cols-2 gap-6" data-id="element-3830">
                {[1, 2].map((_, idx) => <div key={idx} className="bg-stone-200 rounded-2xl aspect-video flex items-center justify-center overflow-hidden" data-id="element-3831">
                    <div className="text-center" data-id="element-3832">
                      <div className="w-14 h-14 bg-white/80 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm" data-id="element-3833">
                        <Play className="w-6 h-6 text-forest-600 ml-0.5" data-id="element-3834" />
                      </div>
                      <p className="text-stone-500 text-sm" data-id="element-3835">
                        {t('services.smileMakeover.video.placeholder')}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </>}

      {/* Related Services */}
      {related.length > 0 && <div className="bg-ivory-200 py-12 border-t border-beige/60" data-id="element-3836">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3837">
            <h2 className="text-2xl font-bold text-charcoal mb-6" data-id="element-3838">
              {lang === 'en' ? 'Related Services' : lang === 'ua' ? 'Інші послуги' : lang === 'ro' ? 'Servicii Conexe' : 'Другие услуги'}
            </h2>
            <div className="grid sm:grid-cols-3 gap-4" data-id="element-3839">
              {related.map(relId => {
            const relKey = relId.replace(/-([a-z])/g, g => g[1].toUpperCase());
            const relTitle = lang === 'ua' && uaServiceTitles[relId] ? uaServiceTitles[relId] : t(`services.${relKey}.title`);
            return <Link key={relId} to={`/${lang}/services/${relId}`} className="group" data-id="element-3840">
                    <div className="bg-ivory p-5 rounded-xl border border-beige/60 hover:border-forest-200 hover:shadow-md transition-all" data-id="element-3841">
                      <h3 className="font-semibold text-charcoal group-hover:text-forest-600 transition-colors mb-1" data-id="element-3842">
                        {relTitle}
                      </h3>
                      <p className="text-sm text-stone-500 line-clamp-1" data-id="element-3843">
                        {t(`services.${relKey}.desc`)}
                      </p>
                      <span className="text-sm text-forest-600 font-medium mt-2 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3844">
                        {t('services.learnMore')}{' '}
                        <ArrowRight className="w-3 h-3" data-id="element-3845" />
                      </span>
                    </div>
                  </Link>;
          })}
            </div>
          </div>
        </div>}

      {/* Multilingual Service FAQ */}
      {serviceFaqs.length > 0 && <div className="bg-ivory py-12 border-t border-beige/60" data-id="element-3846">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3847">
            <h2 className="text-2xl font-bold text-charcoal mb-6" data-id="element-3848">
              {lang === 'en' ? 'Frequently Asked Questions' : lang === 'ua' ? 'Часті запитання' : lang === 'ru' ? 'Частые вопросы' : 'Întrebări frecvente'}
            </h2>
            <div className="space-y-1" data-id="element-3849">
              {serviceFaqs.map((faq, idx) => <ServiceFaqItem key={idx} question={faq.q[lang as 'en' | 'ua' | 'ru' | 'ro'] || faq.q.en} answer={faq.a[lang as 'en' | 'ua' | 'ru' | 'ro'] || faq.a.en} isOpen={openFaqIndex === idx} onToggle={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)} data-id="element-3850" />)}
            </div>
          </div>
        </div>}

      {/* Callback Form */}
      <div className="py-16 bg-ivory" data-id="element-3851">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3852">
          <CallbackForm data-id="element-3853" />
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-forest-900 py-16" data-id="element-3854">
        <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-3855">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-6" data-id="element-3856">
            {t('servicesPage.readyToBook')}
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-3857">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-3858">
              <Button variant="primaryInverse" size="lg" className="gap-2 w-full sm:w-auto" data-id="element-3859">
                <Calendar className="w-5 h-5" data-id="element-3860" />
                {t('servicesPage.bookOnlineNow')}
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-3861">
              <Button variant="whatsappInverse" size="lg" className="gap-2 w-full sm:w-auto" data-id="element-3862">
                <MessageCircle className="w-5 h-5" data-id="element-3863" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </div>
    </Layout>;
}